/**
 * Organization → Users: tenant user directory (add / import / delete).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import adminStyles from "../ui/AdminStyles";
import { StatusBadge, renderCustomerRoles } from "../ui/RoleBadges";
import TableColumnMultiSelectFilter from "../components/TableColumnMultiSelectFilter";
import AddUserModal from "../components/AddUserModal";
import ImportUsersModal from "../components/ImportUsersModal";
import {
  USER_FILTER_NONE,
  USER_TABLE_STATUS_FILTER_OPTIONS,
  userMatchesStatusColumnFilter,
  userMatchesOrNoneMultiFilter,
  userCreatedAtTimestampMs,
} from "../ui/userTableFilterUtils";
import { authedDelete, authedGet, authedPost } from "../api/customerAuthedFetch";
import InfoTip from "../components/InfoTip";
import { ThWithTip } from "../components/FieldTip";

const TIPS = {
  addUser: "Invite a new user to this organization. They receive an email to set their password.",
  importUsers: "Bulk-create users from a CSV file. Existing usernames are skipped or reported.",
  deleteUsers:
    "Permanently remove the selected users from this organization. This cannot be undone.",
  search: "Filter the directory by username, name, or email (case-insensitive).",
  select: "Select rows for bulk delete. Use the header checkbox to select all visible users.",
  username: "Sign-in username for this portal account.",
  name: "Display name shown in the directory and notification recipient lists.",
  email: "Email used for invites, password resets, and notification delivery.",
  status: "Whether the account can sign in (Enabled) or is blocked (Disabled).",
  roles:
    "Business roles that control which portal pages and actions this user can use. Hover a role badge or open a user profile for role help.",
  authStatus:
    "Cognito authentication state (for example Confirmed, Force change password, or Unconfirmed).",
  createdAt: "When the user was created. Click the header to sort newest or oldest first.",
};

const SECTION_CARD = {
  marginTop: 20,
  padding: "20px 22px 22px",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  background: "linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05), 0 1px 3px rgba(15, 23, 42, 0.04)",
};

function customerUserRowId(u) {
  return String(u?.user_id || u?.username || "").trim();
}

export default function CustomerUsers() {
  const navigate = useNavigate();
  const location = useLocation();
  const shouldReloadUsers = !!location.state?.reloadUsers;

  const [customerId, setCustomerId] = useState("");
  const [customerDisplayName, setCustomerDisplayName] = useState("");
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState("");

  const [userSearch, setUserSearch] = useState("");
  const [userFilterStatus, setUserFilterStatus] = useState([]);
  const [userFilterCustomerRoles, setUserFilterCustomerRoles] = useState([]);
  const [userFilterAuthStatus, setUserFilterAuthStatus] = useState([]);
  const [userTableCreatedAtSort, setUserTableCreatedAtSort] = useState("none");
  const [customerUsers, setCustomerUsers] = useState([]);
  const [customerUsersLoading, setCustomerUsersLoading] = useState(false);
  const [customerUsersError, setCustomerUsersError] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [deleteUsersLoading, setDeleteUsersLoading] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showImportUsers, setShowImportUsers] = useState(false);
  const selectAllUsersCheckboxRef = useRef(null);

  useEffect(() => {
    document.title = "Organization · Users";
  }, []);

  useEffect(() => {
    let cancelled = false;
    setProfileLoading(true);
    setProfileError("");
    authedGet("/customer/profile")
      .then((data) => {
        if (cancelled) return;
        setCustomerId(data.customer_id || "");
        setCustomerDisplayName(data.display_name || "");
      })
      .catch((e) => {
        if (!cancelled) setProfileError(String(e.message || e));
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function loadCustomerUsers() {
    setCustomerUsersLoading(true);
    setCustomerUsersError(null);
    try {
      const data = await authedGet("/customer/users?limit=100");
      setCustomerUsers(data.items || []);
    } catch (e) {
      setCustomerUsersError(e.message || "Failed to load users");
    } finally {
      setCustomerUsersLoading(false);
    }
  }

  useEffect(() => {
    loadCustomerUsers();
  }, [shouldReloadUsers]);

  useEffect(() => {
    if (location.state?.reloadUsers) {
      navigate(location.pathname + location.search, { replace: true });
    }
  }, [location.state?.reloadUsers, navigate, location.pathname, location.search]);

  const userTableCustomerRoleFilterOptions = useMemo(() => {
    const ids = new Set();
    for (const u of customerUsers) {
      for (const r of u.roles || []) {
        if (r != null && String(r).trim() !== "") ids.add(String(r));
      }
    }
    const sorted = [...ids].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
    return [
      { id: USER_FILTER_NONE, label: "(none)" },
      ...sorted.map((id) => ({ id, label: id })),
    ];
  }, [customerUsers]);

  const userTableAuthStatusFilterOptions = useMemo(() => {
    const ids = new Set();
    for (const u of customerUsers) {
      ids.add(u.auth_status || "UNKNOWN");
    }
    return [...ids]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .map((id) => ({ id, label: id }));
  }, [customerUsers]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    let rows = customerUsers;
    if (q) {
      rows = rows.filter((u) =>
        [u.username, u.email, u.name, ...(u.roles || [])]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }
    rows = rows.filter((u) => userMatchesStatusColumnFilter(u, userFilterStatus));
    rows = rows.filter((u) =>
      userMatchesOrNoneMultiFilter(u, userFilterCustomerRoles, (x) =>
        (Array.isArray(x.roles) ? x.roles : [])
          .filter((r) => r != null && String(r).trim() !== "")
          .map(String)
      )
    );
    if (userFilterAuthStatus.length) {
      rows = rows.filter((u) =>
        userFilterAuthStatus.includes(u.auth_status || "UNKNOWN")
      );
    }
    return rows;
  }, [
    customerUsers,
    userSearch,
    userFilterStatus,
    userFilterCustomerRoles,
    userFilterAuthStatus,
  ]);

  const usersTableDisplayRows = useMemo(() => {
    const rows = [...filteredUsers];
    if (userTableCreatedAtSort === "none") return rows;
    rows.sort((a, b) => {
      const ta = userCreatedAtTimestampMs(a);
      const tb = userCreatedAtTimestampMs(b);
      if (ta == null && tb == null) return 0;
      if (ta == null) return 1;
      if (tb == null) return -1;
      return userTableCreatedAtSort === "desc" ? tb - ta : ta - tb;
    });
    return rows;
  }, [filteredUsers, userTableCreatedAtSort]);

  const allVisibleUsersSelected = useMemo(() => {
    const visible = usersTableDisplayRows
      .map((u) => customerUserRowId(u))
      .filter(Boolean);
    if (!visible.length) return false;
    return visible.every((id) => selectedUserIds.includes(id));
  }, [usersTableDisplayRows, selectedUserIds]);

  useEffect(() => {
    const valid = new Set(
      customerUsers.map((u) => customerUserRowId(u)).filter(Boolean)
    );
    setSelectedUserIds((prev) => prev.filter((id) => valid.has(id)));
  }, [customerUsers]);

  useEffect(() => {
    const el = selectAllUsersCheckboxRef.current;
    if (!el) return;
    const visible = usersTableDisplayRows
      .map((u) => customerUserRowId(u))
      .filter(Boolean);
    if (visible.length === 0) {
      el.indeterminate = false;
      return;
    }
    const sel = new Set(selectedUserIds);
    let n = 0;
    visible.forEach((id) => {
      if (sel.has(id)) n += 1;
    });
    el.indeterminate = n > 0 && n < visible.length;
  }, [usersTableDisplayRows, selectedUserIds]);

  function toggleSelectAllVisibleUsers() {
    const visible = usersTableDisplayRows
      .map((u) => customerUserRowId(u))
      .filter(Boolean);
    if (!visible.length) return;
    const visSet = new Set(visible);
    const allSelected =
      selectedUserIds.length > 0 &&
      visible.every((id) => selectedUserIds.includes(id));
    if (allSelected) {
      setSelectedUserIds((prev) => prev.filter((id) => !visSet.has(id)));
    } else {
      setSelectedUserIds((prev) => Array.from(new Set([...prev, ...visible])));
    }
  }

  function toggleUserRowSelection(uid) {
    if (!uid) return;
    setSelectedUserIds((prev) =>
      prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]
    );
  }

  async function handleCreateNewUserForCustomer(user) {
    try {
      await authedPost("/customer/users", {
        username: (user.username || "").trim(),
        email: (user.email || "").trim(),
        name: (user.name || "").trim(),
        customerRoles: Array.isArray(user.roles) ? user.roles : [],
      });
      setShowAddUser(false);
      await loadCustomerUsers();
      alert(
        "User created and associated with this customer. An invite email was sent if configured."
      );
    } catch (e) {
      alert(e.message || "Failed to create user");
    }
  }

  async function handleDeleteSelectedUsers() {
    if (selectedUserIds.length === 0) {
      alert("Select at least one user in the table.");
      return;
    }
    const usernames = selectedUserIds
      .map((id) => {
        const row = customerUsers.find((u) => customerUserRowId(u) === id);
        return String(row?.username || id).trim();
      })
      .filter(Boolean);
    if (
      !window.confirm(
        `Permanently delete ${usernames.length} selected user(s)?\n\n` +
          "This removes each user from this customer and from Cognito if they have no other assignments. This cannot be undone."
      )
    ) {
      return;
    }
    setDeleteUsersLoading(true);
    const failed = [];
    try {
      for (const username of usernames) {
        try {
          await authedDelete(`/customer/users/${encodeURIComponent(username)}`);
        } catch (err) {
          failed.push({
            username,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }
      await loadCustomerUsers();
      setSelectedUserIds([]);
      if (failed.length) {
        alert(
          `Some deletes failed:\n${failed.map((f) => `  • ${f.username}: ${f.error}`).join("\n")}`
        );
      }
    } finally {
      setDeleteUsersLoading(false);
    }
  }

  if (profileLoading) return <p>Loading…</p>;
  if (profileError) return <p style={{ color: "red" }}>{profileError}</p>;

  return (
    <div>
      <div
        style={{
          paddingBottom: 18,
          marginBottom: 4,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <h1 style={{ margin: 0 }}>Users</h1>
        {customerDisplayName ? (
          <div style={{ color: "#6b7280", fontSize: 14, marginTop: 4 }}>
            {customerDisplayName}
          </div>
        ) : null}
      </div>

      <section style={{ ...SECTION_CARD, marginBottom: 32 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h3 style={{ margin: 0 }}>Directory</h3>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              alignItems: "center",
            }}
          >
            <button type="button" onClick={() => setShowAddUser(true)}>
              Add User
            </button>
            <InfoTip text={TIPS.addUser} align="end" />
            <button type="button" onClick={() => setShowImportUsers(true)}>
              Import Users
            </button>
            <InfoTip text={TIPS.importUsers} align="end" />
            <button
              type="button"
              disabled={selectedUserIds.length === 0 || deleteUsersLoading}
              onClick={() => void handleDeleteSelectedUsers()}
              style={{ color: "#991b1b", borderColor: "#dc2626" }}
            >
              {deleteUsersLoading ? "Deleting…" : "Delete Users"}
            </button>
            <InfoTip text={TIPS.deleteUsers} align="end" />
          </div>
        </div>

        <div
          style={{
            marginBottom: 8,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <input
            type="text"
            placeholder="Search users…"
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            style={{ width: 280 }}
          />
          <InfoTip text={TIPS.search} />
        </div>

        {customerUsersError && (
          <p style={{ color: "red" }}>{customerUsersError}</p>
        )}

        <table style={adminStyles.table}>
          <thead>
            <tr>
              <th style={{ ...adminStyles.th, width: 36, textAlign: "center" }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    justifyContent: "center",
                  }}
                >
                  <input
                    ref={selectAllUsersCheckboxRef}
                    type="checkbox"
                    title="Select all users shown"
                    checked={allVisibleUsersSelected}
                    disabled={
                      customerUsersLoading || usersTableDisplayRows.length === 0
                    }
                    onChange={() => toggleSelectAllVisibleUsers()}
                  />
                  <InfoTip text={TIPS.select} />
                </span>
              </th>
              <th style={adminStyles.th}>#</th>
              <ThWithTip tip={TIPS.username}>Username</ThWithTip>
              <ThWithTip tip={TIPS.name}>Name</ThWithTip>
              <ThWithTip tip={TIPS.email}>Email</ThWithTip>
              <th style={adminStyles.th}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    alignItems: "flex-start",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    Status
                    <InfoTip text={TIPS.status} />
                  </span>
                  <TableColumnMultiSelectFilter
                    options={USER_TABLE_STATUS_FILTER_OPTIONS}
                    value={userFilterStatus}
                    onChange={setUserFilterStatus}
                    ariaLabel="Filter users by enabled or disabled"
                  />
                </div>
              </th>
              <th style={adminStyles.th}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    alignItems: "flex-start",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    Customer Roles
                    <InfoTip text={TIPS.roles} wide />
                  </span>
                  <TableColumnMultiSelectFilter
                    options={userTableCustomerRoleFilterOptions}
                    value={userFilterCustomerRoles}
                    onChange={setUserFilterCustomerRoles}
                    ariaLabel="Filter users by customer roles"
                  />
                </div>
              </th>
              <th style={adminStyles.th}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    alignItems: "flex-start",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    Auth Status
                    <InfoTip text={TIPS.authStatus} />
                  </span>
                  <TableColumnMultiSelectFilter
                    options={userTableAuthStatusFilterOptions}
                    value={userFilterAuthStatus}
                    onChange={setUserFilterAuthStatus}
                    ariaLabel="Filter users by Cognito auth status"
                  />
                </div>
              </th>
              <th style={adminStyles.th}>
                <button
                  type="button"
                  title="Cycle: default order → newest first → oldest first"
                  onClick={() =>
                    setUserTableCreatedAtSort((s) =>
                      s === "none" ? "desc" : s === "desc" ? "asc" : "none"
                    )
                  }
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    width: "100%",
                    margin: 0,
                    padding: "4px 0",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    font: "inherit",
                    fontWeight: 600,
                    textAlign: "left",
                    color: "#111827",
                  }}
                >
                  Created At
                  {userTableCreatedAtSort === "desc"
                    ? " ↓"
                    : userTableCreatedAtSort === "asc"
                      ? " ↑"
                      : ""}
                  <InfoTip text={TIPS.createdAt} />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {customerUsersLoading && (
              <tr>
                <td colSpan={9} style={{ ...adminStyles.td, textAlign: "center" }}>
                  Loading users…
                </td>
              </tr>
            )}
            {!customerUsersLoading && usersTableDisplayRows.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  style={{ ...adminStyles.td, textAlign: "center", color: "#6b7280" }}
                >
                  No users found
                </td>
              </tr>
            )}
            {usersTableDisplayRows.map((u, idx) => {
              const rowId = customerUserRowId(u);
              return (
                <tr key={rowId || idx}>
                  <td style={{ ...adminStyles.td, textAlign: "center" }}>
                    <input
                      type="checkbox"
                      disabled={!rowId}
                      checked={rowId ? selectedUserIds.includes(rowId) : false}
                      onChange={() => toggleUserRowSelection(rowId)}
                    />
                  </td>
                  <td style={adminStyles.td}>{idx + 1}</td>
                  <td style={adminStyles.td}>
                    <button
                      type="button"
                      style={adminStyles.linkButton}
                      onClick={() =>
                        navigate(
                          `/organization/users/${encodeURIComponent(u.username)}`,
                          {
                            state: { from: { pathname: "/organization/users" } },
                          }
                        )
                      }
                    >
                      {u.username}
                    </button>
                  </td>
                  <td style={adminStyles.td}>{u.name || "—"}</td>
                  <td style={adminStyles.td}>{u.email || "—"}</td>
                  <td style={adminStyles.td}>
                    <span
                      style={{
                        padding: "2px 6px",
                        borderRadius: 6,
                        background: u.enabled ? "#dcfce7" : "#fee2e2",
                        color: u.enabled ? "#166534" : "#991b1b",
                        fontWeight: 600,
                        fontSize: 12,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {u.enabled ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                  <td style={adminStyles.td}>{renderCustomerRoles(u.roles)}</td>
                  <td style={adminStyles.td}>
                    <StatusBadge status={u.auth_status || "UNKNOWN"} />
                  </td>
                  <td style={adminStyles.td}>
                    {u.created_at
                      ? new Date(u.created_at).toLocaleString()
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {showAddUser && (
        <AddUserModal
          open={showAddUser}
          customerId={customerId}
          onClose={() => setShowAddUser(false)}
          onCreateNew={handleCreateNewUserForCustomer}
        />
      )}

      {showImportUsers && (
        <ImportUsersModal
          open={showImportUsers}
          customerDisplayName={customerDisplayName}
          onClose={() => setShowImportUsers(false)}
          onSuccess={() => {
            setShowImportUsers(false);
            loadCustomerUsers();
          }}
        />
      )}
    </div>
  );
}
